"use client";

import { c as _c } from "react/compiler-runtime";
import UserAvatar from "@/app/(v2)/_components/UserAvatar";
import ResponsiveModal from "@/components/custom/ResponsiveModal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { GlassIcon } from "@/components/ui/glass-icon";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { Switch } from "@/components/ui/switch";
import { useFileUpload } from "@/hooks/useFileUpload";
import { clearClientSessionCookies } from "@/lib/authenticatedFetch";
import { getLastSignInMethod } from "@/lib/lastSignInMethod";
import { ROUTES } from "@/lib/routes";
import { toast } from "@/lib/toast";
import { cn, formatUsername } from "@/lib/utils";
import { useManageAccountModalStore } from "@/stores/manageAccountModalStore";
import app from "@/utils/feathers";
import type { Users } from "@b3dotfun/b3-api";
import { useAuthentication, useBetterAuth, useProfile } from "@b3dotfun/sdk/global-account/react";
import { IconArrowBoxRight } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconCheckOutline18, IconCircleCheckOutline18, IconCircleXmarkOutline18, IconEnvelopePenOutline18, IconKeyOutline18, IconLockPasswordOutline18, IconRefreshOutline18, IconShieldHalvedOutline18, IconUserOutline18 } from "nucleo-ui-outline-18";
import { type ChangeEvent, type ComponentType, type FormEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
const PARTNER_ID = process.env.NEXT_PUBLIC_GLOBAL_ACCOUNTS_PARTNER_ID ?? "";
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
type AuthError = {
  code?: string;
  message?: string;
  status?: number;
  statusText?: string;
};
type AuthResult<T> = {
  data: T | null;
  error: AuthError | null;
};
type BetterAuthAccount = {
  id: string;
  providerId: string;
  accountId: string;
  userId: string;
  scopes?: string[];
  createdAt?: Date | string;
  updatedAt?: Date | string;
};
type BetterAuthUser = {
  id: string;
  email: string;
  emailVerified: boolean;
  name: string;
  image?: string | null;
  createdAt?: Date | string;
  updatedAt?: Date | string;
};
type BetterAuthSession = {
  user: BetterAuthUser;
  session?: {
    id?: string;
    token?: string;
    createdAt?: Date | string;
    updatedAt?: Date | string;
    expiresAt?: Date | string;
  };
};
type BetterAuthClientForManageAccount = {
  getSession: () => Promise<AuthResult<BetterAuthSession | null>>;
  listAccounts: () => Promise<AuthResult<BetterAuthAccount[]>>;
  updateUser: (input: {
    image?: string | null;
    name?: string;
  }) => Promise<AuthResult<{
    status: boolean;
  }>>;
  changeEmail: (input: {
    newEmail: string;
    callbackURL?: string;
  }) => Promise<AuthResult<{
    status: boolean;
    message?: string | null;
  }>>;
  changePassword: (input: {
    currentPassword: string;
    newPassword: string;
    revokeOtherSessions?: boolean;
  }) => Promise<AuthResult<{
    token: string | null;
    user: BetterAuthUser;
  }>>;
  setPassword: (input: {
    newPassword: string;
  }) => Promise<AuthResult<{
    status: boolean;
  }>>;
  sendVerificationEmail: (input: {
    email: string;
    callbackURL?: string;
  }) => Promise<AuthResult<{
    status: boolean;
  }>>;
};
type UsersService = {
  registerUsername: (data: {
    username: string;
  }, context: Record<string, never>) => Promise<Users | Users[]>;
  setAvatar: (data: {
    avatar: string;
  }, context: Record<string, never>) => Promise<Users | Users[]>;
};
type SectionProps = {
  title: string;
  icon: ComponentType<{
    className?: string;
  }>;
  children: ReactNode;
};
function buildCallbackUrl(route: string): string | undefined {
  return typeof window === "undefined" ? undefined : `${window.location.origin}${route}`;
}
export function ManageAccountModalHost() {
  const $ = _c(3);
  const open = useManageAccountModalStore(_temp);
  const setManageAccountOpen = useManageAccountModalStore(_temp2);
  let t0;
  if ($[0] !== open || $[1] !== setManageAccountOpen) {
    t0 = <ManageAccountModal open={open} onOpenChange={setManageAccountOpen} />;
    $[0] = open;
    $[1] = setManageAccountOpen;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}
function _temp2(state_0) {
  return state_0.setManageAccountOpen;
}
function _temp(state) {
  return state.open;
}
function ManageAccountModal({
  open,
  onOpenChange
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const {
    user,
    setUser,
    logout
  } = useAuthentication(PARTNER_ID, {
    skipAutoConnect: true
  });
  const {
    betterAuthClient,
    requestPasswordReset,
    exchangeForFeathersJWT
  } = useBetterAuth();
  const authClientRef = useRef<BetterAuthClientForManageAccount | null>(null);
  authClientRef.current = betterAuthClient as unknown as BetterAuthClientForManageAccount;
  const {
    data: profileData,
    refetch: refreshProfile,
    isFetching: profileLoading
  } = useProfile({
    address: undefined,
    name: undefined,
    b3GlobalId: user?.userId,
    fresh: true
  }, {
    enabled: Boolean(user?.userId)
  });
  const {
    uploadFile,
    isUploading: avatarUploading
  } = useFileUpload({
    maxSizeInMB: 5,
    acceptedTypes: ["image/jpeg", "image/png", "image/webp", "image/gif"]
  });
  const [sessionUser, setSessionUser] = useState<BetterAuthUser | null>(null);
  const [accounts, setAccounts] = useState<BetterAuthAccount[]>([]);
  const [authDetailsLoading, setAuthDetailsLoading] = useState(false);
  const [usernameInput, setUsernameInput] = useState("");
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState<string | null>(null);
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [usernameSaving, setUsernameSaving] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [revokeOtherSessions, setRevokeOtherSessions] = useState(true);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [resetSending, setResetSending] = useState(false);
  const [verificationSending, setVerificationSending] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [lastSignInMethod, setLastSignInMethod] = useState<ReturnType<typeof getLastSignInMethod>>(null);
  const avatarObjectUrlRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const usersService = useMemo(() => (app as any).service("users") as unknown as UsersService, []);
  const currentEmail = sessionUser?.email || user?.email || "";
  const currentB3Username = normalizeEditableUsername(profileData?.name || user?.username || "");
  const formattedB3Username = formatUsername(profileData?.name || user?.username);
  const displayName = formattedB3Username || sessionUser?.name || currentEmail || "Account";
  const currentAvatar = profileData?.avatar || user?.avatar || sessionUser?.image || null;
  const hasCredentialAccount = accounts.some(account => isCredentialProvider(account.providerId));
  const lastProviderId = lastSignInMethod === "email" ? "credential" : lastSignInMethod;
  const primaryProviderId = accounts.find(account_0 => account_0.providerId === lastProviderId)?.providerId || accounts[0]?.providerId || null;
  const getAuthClient = useCallback(() => {
    const client = authClientRef.current;
    if (!client) {
      throw new Error("Authentication client is not available");
    }
    return client;
  }, []);
  const loadAuthDetails = useCallback(async () => {
    if (!open) return;
    setAuthDetailsLoading(true);
    setLastSignInMethod(getLastSignInMethod());
    try {
      const authClient = getAuthClient();
      const [sessionResult, accountsResult] = await Promise.all([authClient.getSession(), authClient.listAccounts()]);
      const session = unwrapAuthResult(sessionResult, "Could not load your account session.");
      const accountList = unwrapAuthResult(accountsResult, "Could not load sign-in methods.");
      setSessionUser(session?.user ?? null);
      setAccounts(accountList ?? []);
    } catch (error) {
      console.error("@@B3OS: Failed to load account details:", error);
      toast.error(getErrorMessage(error, "Failed to load account details"));
      setSessionUser(null);
      setAccounts([]);
    } finally {
      setAuthDetailsLoading(false);
    }
  }, [getAuthClient, open]);
  useEffect(() => {
    if (!open) return;
    void loadAuthDetails();
  }, [loadAuthDetails, open]);
  useEffect(() => {
    if (!open) return;
    setUsernameInput(currentB3Username);
  }, [currentB3Username, open]);
  useEffect(() => {
    if (open) return;
    setNewEmail("");
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setAvatarPreviewUrl(null);
    if (avatarObjectUrlRef.current) {
      URL.revokeObjectURL(avatarObjectUrlRef.current);
      avatarObjectUrlRef.current = null;
    }
  }, [open]);
  useEffect(() => {
    return () => {
      if (avatarObjectUrlRef.current) {
        URL.revokeObjectURL(avatarObjectUrlRef.current);
      }
    };
  }, []);
  const applyUpdatedUser = useCallback((updatedUser: Users | Users[]) => {
    setUser(Array.isArray(updatedUser) ? updatedUser[0] : updatedUser);
  }, [setUser]);
  const handleAvatarFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (avatarObjectUrlRef.current) {
      URL.revokeObjectURL(avatarObjectUrlRef.current);
    }
    const objectUrl = URL.createObjectURL(file);
    avatarObjectUrlRef.current = objectUrl;
    setAvatarPreviewUrl(objectUrl);
    const ipfsUrl = await uploadFile(file);
    if (!ipfsUrl) {
      setAvatarPreviewUrl(null);
      return;
    }
    setAvatarSaving(true);
    try {
      const authClient_0 = getAuthClient();
      const updatedUser_0 = await usersService.setAvatar({
        avatar: ipfsUrl
      }, {});
      applyUpdatedUser(updatedUser_0);
      await ignoreAuthUpdateError(() => authClient_0.updateUser({
        image: ipfsUrl
      }));
      await Promise.all([refreshProfile(), loadAuthDetails()]);
      setAvatarPreviewUrl(null);
      toast.success("Profile photo updated");
    } catch (error_0) {
      console.error("@@B3OS: Failed to update avatar:", error_0);
      toast.error(getErrorMessage(error_0, "Failed to update profile photo"));
    } finally {
      setAvatarSaving(false);
    }
  };
  const handleSaveUsername = async (event_0: FormEvent<HTMLFormElement>) => {
    event_0.preventDefault();
    const normalizedUsername = normalizeEditableUsername(usernameInput);
    if (!normalizedUsername) {
      toast.error("Enter a username");
      return;
    }
    if (normalizedUsername === currentB3Username) {
      toast.info("Username is already up to date");
      return;
    }
    setUsernameSaving(true);
    try {
      const updatedUser_1 = await usersService.registerUsername({
        username: normalizedUsername
      }, {});
      applyUpdatedUser(updatedUser_1);
      await refreshProfile();
      setUsernameInput(normalizedUsername);
      toast.success("Username updated");
    } catch (error_1) {
      console.error("@@B3OS: Failed to update username:", error_1);
      toast.error(getErrorMessage(error_1, "Failed to update username"));
    } finally {
      setUsernameSaving(false);
    }
  };
  const handleChangeEmail = async (event_1: FormEvent<HTMLFormElement>) => {
    event_1.preventDefault();
    const nextEmail = newEmail.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(nextEmail)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (nextEmail === currentEmail.toLowerCase()) {
      toast.info("That email is already on your account");
      return;
    }
    setEmailSaving(true);
    try {
      const authClient_1 = getAuthClient();
      const result = await authClient_1.changeEmail({
        newEmail: nextEmail,
        callbackURL: buildCallbackUrl(ROUTES.VERIFY_EMAIL)
      });
      unwrapAuthResult(result, "Failed to change email");
      setNewEmail("");
      await loadAuthDetails();
      toast.success("Check your email to confirm the change");
    } catch (error_2) {
      console.error("@@B3OS: Failed to change email:", error_2);
      toast.error(getErrorMessage(error_2, "Failed to change email"));
    } finally {
      setEmailSaving(false);
    }
  };
  const handleResendVerification = async () => {
    if (!currentEmail) return;
    setVerificationSending(true);
    try {
      const authClient_2 = getAuthClient();
      const result_0 = await authClient_2.sendVerificationEmail({
        email: currentEmail,
        callbackURL: buildCallbackUrl(ROUTES.VERIFY_EMAIL)
      });
      unwrapAuthResult(result_0, "Failed to send verification email");
      toast.success("Verification email sent — check your inbox");
    } catch (error_3) {
      console.error("@@B3OS: Failed to send verification email:", error_3);
      toast.error(getErrorMessage(error_3, "Failed to send verification email"));
    } finally {
      setVerificationSending(false);
    }
  };
  const handleSavePassword = async (event_2: FormEvent<HTMLFormElement>) => {
    event_2.preventDefault();
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (hasCredentialAccount && !currentPassword) {
      toast.error("Enter your current password");
      return;
    }
    setPasswordSaving(true);
    try {
      const authClient_3 = getAuthClient();
      if (hasCredentialAccount) {
        const result_1 = await authClient_3.changePassword({
          currentPassword,
          newPassword,
          revokeOtherSessions
        });
        const data = unwrapAuthResult(result_1, "Failed to update password");
        if (data?.token) {
          await exchangeForFeathersJWT(data.token);
        }
      } else {
        const result_2 = await authClient_3.setPassword({
          newPassword
        });
        unwrapAuthResult(result_2, "Failed to create password");
        await loadAuthDetails();
      }
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success(hasCredentialAccount ? "Password updated" : "Password created");
    } catch (error_4) {
      console.error("@@B3OS: Failed to update password:", error_4);
      toast.error(getErrorMessage(error_4, "Failed to update password"));
    } finally {
      setPasswordSaving(false);
    }
  };
  const handleSendPasswordReset = async () => {
    if (!currentEmail) {
      toast.error("No email address is available for this account");
      return;
    }
    setResetSending(true);
    try {
      await requestPasswordReset(currentEmail, buildCallbackUrl(ROUTES.RESET_PASSWORD));
      toast.success("Password reset email sent");
    } catch (error_5) {
      console.error("@@B3OS: Failed to send password reset:", error_5);
      toast.error(getErrorMessage(error_5, "Failed to send password reset email"));
    } finally {
      setResetSending(false);
    }
  };
  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await logout();
    } finally {
      clearClientSessionCookies();
      onOpenChange(false);
      window.location.replace(ROUTES.HOME);
    }
  };
  return <ResponsiveModal open={open} onOpenChange={onOpenChange} title="Manage Account" contentClass="max-w-3xl" innerClass="flex max-h-[min(820px,calc(100vh-80px))] flex-col gap-0 overflow-hidden p-0">
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-3 sm:px-7">
        <Section title="Profile" icon={IconUserOutline18}>
          <div className="divide-ds-border-default divide-y">
            <div className="grid gap-3 pb-5 sm:grid-cols-[150px_minmax(0,1fr)] sm:items-center">
              <div>
                <p className="text-ds-text-secondary text-sm font-medium">Profile photo</p>
                <p className="text-ds-text-muted mt-1 text-xs">PNG, JPG, WebP, or GIF.</p>
              </div>
              <div className="flex min-w-0 items-center gap-4">
                <div className="relative shrink-0">
                  <div className="bg-ds-neutral-100 dark:bg-ds-neutral-800 border-ds-border-default h-20 w-20 overflow-hidden rounded-full border">
                    {avatarPreviewUrl ? <img src={avatarPreviewUrl} alt="" className="h-full w-full object-cover" /> : <UserAvatar avatarUrl={currentAvatar} name={displayName} b3GlobalId={user?.userId} size={80} />}
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={handleAvatarFileChange} />
                </div>
                <div className="relative flex min-w-0 items-center">
                  <Button variant="white" size="sm" disabled={avatarUploading || avatarSaving} onClick={() => fileInputRef.current?.click()} className="w-fit">
                    {avatarUploading || avatarSaving ? <PixelLoader size="sm" color="blue" /> : "Change Avatar"}
                  </Button>
                  {(avatarUploading || avatarSaving) && <div className="absolute left-0 top-full mt-2 flex items-center gap-2 whitespace-nowrap">
                      <PixelLoader size="xs" color="blue" />
                      <span className="text-ds-text-muted text-xs">
                        {avatarUploading ? "Uploading photo" : "Saving photo"}
                      </span>
                    </div>}
                </div>
              </div>
            </div>

            <form onSubmit={handleSaveUsername} className="grid gap-3 pt-5 sm:grid-cols-[150px_minmax(0,1fr)] sm:items-center">
              <Label htmlFor="manage-account-username" className="text-ds-text-secondary">
                Username
              </Label>
              <div className="min-w-0">
                <div className="flex gap-2">
                  <div className="relative min-w-0 flex-1">
                    <span className="text-ds-text-muted pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm">
                      @
                    </span>
                    <Input id="manage-account-username" value={usernameInput} onChange={event_3 => setUsernameInput(event_3.target.value)} className="pl-7" placeholder="username" disabled={usernameSaving || profileLoading} />
                  </div>
                  <Button type="submit" variant="brand" size="sm" disabled={usernameSaving || normalizeEditableUsername(usernameInput) === currentB3Username}>
                    {usernameSaving ? <PixelLoader size="sm" color="white" /> : "Save"}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </Section>

        {(authDetailsLoading || accounts.length > 0) && <Section title="Sign-in Methods" icon={IconShieldHalvedOutline18}>
            {authDetailsLoading ? <div className="flex items-center gap-2 py-2">
                <PixelLoader size="sm" color="blue" />
                <span className="text-ds-text-muted text-sm">Loading sign-in methods</span>
              </div> : <div className="divide-ds-border-default divide-y">
                {accounts.map(account_1 => <div key={account_1.id} className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
                    <div className="flex min-w-0 items-center gap-3">
                      <ProviderBadge providerId={account_1.providerId} />
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-ds-text-primary text-sm font-medium">
                            {getProviderLabel(account_1.providerId)}
                          </p>
                          {account_1.providerId === primaryProviderId && <StatusPill tone="brand">Last used</StatusPill>}
                        </div>
                        <p className="text-ds-text-muted truncate text-xs">
                          {isCredentialProvider(account_1.providerId) ? currentEmail || "Email login" : account_1.accountId}
                        </p>
                      </div>
                    </div>
                    {formatProviderDate(account_1.createdAt) && <p className="text-ds-text-muted hidden shrink-0 text-xs sm:block">
                        {formatProviderDate(account_1.createdAt)}
                      </p>}
                  </div>)}
              </div>}
          </Section>}

        <Section title="Email" icon={IconEnvelopePenOutline18}>
          <div className="space-y-4">
            <div className="border-ds-border-default flex flex-wrap items-center justify-between gap-3 border-b pb-4">
              <div className="min-w-0">
                <p className="text-ds-text-muted text-xs">Current email</p>
                <p className="text-ds-text-primary truncate text-sm font-medium">
                  {currentEmail || "No email on file"}
                </p>
              </div>
              {sessionUser?.emailVerified ? <StatusPill tone="success">
                  <IconCircleCheckOutline18 className="h-3.5 w-3.5" />
                  Verified
                </StatusPill> : <div className="flex items-center gap-2">
                  <Badge variant="warning" className="gap-1 normal-case tracking-normal">
                    <IconCircleXmarkOutline18 className="h-3.5 w-3.5" />
                    Unverified
                  </Badge>
                  {currentEmail && <Button type="button" variant="ghost" size="sm" className="text-ds-text-link h-auto px-1.5 py-0.5 text-xs" disabled={verificationSending} onClick={handleResendVerification}>
                      {verificationSending ? <PixelLoader size="sm" color="blue" /> : <>
                          <IconRefreshOutline18 className="h-3 w-3" />
                          Verify
                        </>}
                    </Button>}
                </div>}
            </div>

            <form onSubmit={handleChangeEmail} className="space-y-2">
              <Label htmlFor="manage-account-email" className="text-ds-text-secondary">
                New email
              </Label>
              <div className="flex gap-2">
                <Input id="manage-account-email" type="email" value={newEmail} onChange={event_4 => setNewEmail(event_4.target.value)} placeholder="you@example.com" disabled={emailSaving} />
                <Button type="submit" variant="brand" size="sm" disabled={emailSaving || !newEmail.trim()}>
                  {emailSaving ? <PixelLoader size="sm" color="white" /> : "Change"}
                </Button>
              </div>
            </form>
          </div>
        </Section>

        <Section title={authDetailsLoading || hasCredentialAccount ? "Password" : "Create Password"} icon={authDetailsLoading || hasCredentialAccount ? IconLockPasswordOutline18 : IconKeyOutline18}>
          {authDetailsLoading ? <div className="flex items-center gap-2 py-2">
              <PixelLoader size="sm" color="blue" />
              <span className="text-ds-text-muted text-sm">Loading password settings</span>
            </div> : <form onSubmit={handleSavePassword} className="space-y-3">
              {hasCredentialAccount && <div className="space-y-2">
                  <Label htmlFor="manage-account-current-password" className="text-ds-text-secondary">
                    Current password
                  </Label>
                  <Input id="manage-account-current-password" type="password" value={currentPassword} onChange={event_5 => setCurrentPassword(event_5.target.value)} autoComplete="current-password" disabled={passwordSaving} />
                </div>}

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="manage-account-new-password" className="text-ds-text-secondary">
                    New password
                  </Label>
                  <Input id="manage-account-new-password" type="password" value={newPassword} onChange={event_6 => setNewPassword(event_6.target.value)} autoComplete="new-password" disabled={passwordSaving} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="manage-account-confirm-password" className="text-ds-text-secondary">
                    Confirm password
                  </Label>
                  <Input id="manage-account-confirm-password" type="password" value={confirmPassword} onChange={event_7 => setConfirmPassword(event_7.target.value)} autoComplete="new-password" disabled={passwordSaving} />
                </div>
              </div>

              {hasCredentialAccount && <label className="border-ds-border-default flex cursor-pointer items-center justify-between gap-3 border-y py-3">
                  <span className="text-ds-text-secondary text-sm">Sign out other sessions</span>
                  <Switch checked={revokeOtherSessions} onCheckedChange={setRevokeOtherSessions} size="sm" />
                </label>}

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <Button type="button" variant="white" size="sm" disabled={resetSending || !currentEmail} onClick={handleSendPasswordReset}>
                  {resetSending ? <PixelLoader size="sm" color="blue" /> : <IconRefreshOutline18 className="h-4 w-4" />}
                  Send Reset Link
                </Button>
                <Button type="submit" variant="brand" size="sm" disabled={passwordSaving || !newPassword || !confirmPassword}>
                  {passwordSaving ? <PixelLoader size="sm" color="white" /> : <IconCheckOutline18 className="h-4 w-4" />}
                  {hasCredentialAccount ? "Update Password" : "Create Password"}
                </Button>
              </div>
            </form>}
        </Section>
      </div>

      <div className="border-ds-border-default flex shrink-0 flex-col-reverse gap-2 border-t px-5 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4 sm:flex-row sm:items-center sm:justify-between sm:px-7">
        <Button variant="white" size="sm" onClick={handleSignOut} disabled={signingOut}>
          {signingOut ? <PixelLoader size="sm" color="blue" /> : <IconArrowBoxRight className="h-4 w-4" />}
          Sign Out
        </Button>
        <Button variant="brand" size="sm" onClick={() => onOpenChange(false)}>
          Done
        </Button>
      </div>
    </ResponsiveModal>;
}
function Section(t0) {
  const $ = _c(12);
  const {
    title,
    icon: Icon,
    children
  } = t0;
  let t1;
  if ($[0] !== Icon) {
    t1 = <GlassIcon icon={Icon} color="zinc" size="md" />;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== title) {
    t2 = <div className="min-w-0"><h3 className="text-ds-text-primary text-base font-semibold">{title}</h3></div>;
    $[2] = title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex items-center gap-3 md:pr-2">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== children) {
    t4 = <div className="min-w-0">{children}</div>;
    $[7] = children;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <section className="border-ds-border-default grid items-start gap-4 border-b py-5 last:border-b-0 md:grid-cols-[240px_minmax(0,1fr)] md:gap-7">{t3}{t4}</section>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function ProviderBadge(t0) {
  const $ = _c(7);
  const {
    providerId
  } = t0;
  let t1;
  if ($[0] !== providerId) {
    const label = getProviderLabel(providerId);
    t1 = isCredentialProvider(providerId) ? "@" : label.charAt(0).toUpperCase();
    $[0] = providerId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const initial = t1;
  const t2 = isCredentialProvider(providerId) ? "bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300" : "bg-ds-neutral-100 text-ds-text-secondary dark:bg-ds-neutral-800";
  let t3;
  if ($[2] !== t2) {
    t3 = cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-semibold", t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== initial || $[5] !== t3) {
    t4 = <div className={t3}>{initial}</div>;
    $[4] = initial;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
function StatusPill(t0) {
  const $ = _c(7);
  const {
    tone,
    children
  } = t0;
  const t1 = tone === "brand" && "bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300";
  const t2 = tone === "success" && "bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-300";
  const t3 = tone === "muted" && "bg-ds-neutral-100 text-ds-text-muted dark:bg-ds-neutral-800";
  let t4;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3) {
    t4 = cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium", t1, t2, t3);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== children || $[5] !== t4) {
    t5 = <span className={t4}>{children}</span>;
    $[4] = children;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
}
function unwrapAuthResult<T>(result: AuthResult<T>, fallbackMessage: string): T | null {
  if (result.error) {
    throw new Error(result.error.message || result.error.code || fallbackMessage);
  }
  return result.data;
}
async function ignoreAuthUpdateError(task: () => Promise<AuthResult<unknown>>) {
  try {
    const result = await task();
    if (result.error) {
      console.warn("@@B3OS: Account user update failed:", result.error);
    }
  } catch (error) {
    console.warn("@@B3OS: Account user update failed:", error);
  }
}
function normalizeEditableUsername(value: string) {
  return value.trim().replace(/^@+/, "").replace(/\.b3\.fun$/i, "").toLowerCase();
}
function isCredentialProvider(providerId: string) {
  return ["credential", "email", "email-password", "password"].includes(providerId);
}
function getProviderLabel(providerId: string) {
  const labels: Record<string, string> = {
    apple: "Apple",
    credential: "Email and password",
    discord: "Discord",
    email: "Email",
    "email-password": "Email and password",
    github: "GitHub",
    google: "Google",
    microsoft: "Microsoft",
    password: "Password",
    slack: "Slack",
    twitter: "Twitter"
  };
  return labels[providerId] || providerId.replace(/-/g, " ").replace(/\b\w/g, character => character.toUpperCase());
}
function formatProviderDate(value: Date | string | undefined) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric"
  }).format(date);
}
function getErrorMessage(error: unknown, fallbackMessage: string) {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (typeof error === "object" && error !== null && "message" in error) {
    const message = (error as {
      message?: unknown;
    }).message;
    if (typeof message === "string" && message) {
      return message;
    }
  }
  return fallbackMessage;
}